import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Modal, { ModalActionData } from '../components/Common/Modal';
import TextArea from '../components/Common/TextArea';
import TextField from '../components/Common/TextField';
import { DashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { updateVariant } from '../services/DynamicPageVariantsService';

interface Props {
  variantId?: string;
  open: boolean;
  onClose: () => void;
}

export default function EditVariantNameAndDescription({
  variantId,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);
  const { variants } = useContext(DynamicPageContext);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  const variant = useMemo(
    () => (variantId ? variants?.find((v) => v.id === variantId) : undefined),
    [variantId, variants],
  );

  useEffect(() => {
    setName(variant?.data().name || '');
    setDescription(variant?.data().description || '');
  }, [variant]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  const saveNameAndDescription = useCallback(async () => {
    if (saving) return;
    if (!variant) return;
    if (!selectedAccount || !selectedDynamicPage) return;

    setSaving(true);

    try {
      const update = {
        name: name,
        description: description,
      };
      await updateVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variant.id,
        update,
      );
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    saving,
    variant,
    selectedAccount,
    selectedDynamicPage,
    name,
    description,
    onClose,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        onClick: () => saveNameAndDescription(),
      },
    ],
    [saving, onClose, saveNameAndDescription],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="lg">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Update variant name and description
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextArea
          title="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
